(() => {
  const doc = document;
  const body = doc.body;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const qs = (selector, scope = doc) => scope.querySelector(selector);
  const qsa = (selector, scope = doc) => Array.from(scope.querySelectorAll(selector));
  const prepare = (element, key) => {
    if (!element || element.dataset[key] === 'true') return false;
    element.dataset[key] = 'true';
    return true;
  };

  const formatMoney = (cents, format) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format);
    }
    const currency = doc.documentElement.dataset.currency || 'CAD';
    return new Intl.NumberFormat(doc.documentElement.lang || 'en-CA', {
      style: 'currency',
      currency
    }).format(cents / 100);
  };

  let lastCartTrigger = null;
  let cartCloseTimer = null;

  const initHeader = () => {
    const header = qs('[data-irwin-header]');
    const menuButton = qs('[data-menu-button]');
    const mobileNav = qs('[data-mobile-nav]');
    if (!prepare(header, 'irwinHeaderReady')) return;

    const updateHeader = () => {
      if (header) header.classList.toggle('scrolled', window.scrollY > 24);
      if (header && mobileNav) {
        const navTop = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
        mobileNav.style.setProperty('--irwin-mobile-nav-top', `${navTop}px`);
      }
    };

    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });

    if (!menuButton || !mobileNav) return;

    const closeMenu = () => {
      menuButton.setAttribute('aria-expanded', 'false');
      mobileNav.setAttribute('aria-hidden', 'true');
      mobileNav.classList.remove('open');
      body.classList.remove('irwin-menu-open');
    };

    menuButton.addEventListener('click', () => {
      const opening = menuButton.getAttribute('aria-expanded') !== 'true';
      menuButton.setAttribute('aria-expanded', String(opening));
      mobileNav.setAttribute('aria-hidden', String(!opening));
      mobileNav.classList.toggle('open', opening);
      body.classList.toggle('irwin-menu-open', opening);
      if (opening) updateHeader();
    });

    qsa('a', mobileNav).forEach((link) => link.addEventListener('click', closeMenu));
    doc.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && mobileNav.classList.contains('open')) {
        closeMenu();
        menuButton.focus();
      }
    });
    window.addEventListener('resize', () => {
      if (window.innerWidth > 1180) closeMenu();
      updateHeader();
    });
  };

  let lastSearchTrigger = null;

  const initSearch = () => {
    const overlay = qs('[data-search-overlay]');
    if (!prepare(overlay, 'irwinSearchReady')) return;
    const panel = qs('.irwin-search__panel', overlay);
    const input = qs('[data-search-input]', overlay);
    const form = qs('.irwin-search__form', overlay);
    const results = qs('[data-search-results]', overlay);
    const resultsContent = qs('[data-search-results-content]', overlay);
    const loading = qs('[data-search-loading]', overlay);
    const predictiveSearchUrl = overlay.dataset.predictiveSearchUrl;
    const focusableSelector = 'button:not([disabled]),input:not([disabled]),a[href],[tabindex]:not([tabindex="-1"])';
    let searchTimer = null;
    let searchRequest = null;

    const setResultsExpanded = (expanded) => {
      if (results) results.hidden = !expanded;
      input?.setAttribute('aria-expanded', String(expanded));
    };

    const resetResults = () => {
      window.clearTimeout(searchTimer);
      searchRequest?.abort();
      searchRequest = null;
      if (loading) loading.hidden = true;
      if (resultsContent) resultsContent.replaceChildren();
      setResultsExpanded(false);
    };

    const showSearchError = () => {
      if (!resultsContent || !form || !input) return;
      const message = doc.createElement('p');
      message.className = 'irwin-predictive__empty';
      message.textContent = 'Suggestions are temporarily unavailable. Press Enter to search the full store.';
      resultsContent.replaceChildren(message);
      if (loading) loading.hidden = true;
      setResultsExpanded(true);
    };

    const fetchPredictiveResults = async () => {
      const query = input?.value.trim() || '';
      if (!query || !predictiveSearchUrl || !resultsContent) {
        resetResults();
        return;
      }

      searchRequest?.abort();
      const request = new AbortController();
      searchRequest = request;
      const url = new URL(predictiveSearchUrl, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,collection,page');
      url.searchParams.set('resources[limit]', '8');
      url.searchParams.set('resources[limit_scope]', 'all');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');
      url.searchParams.set('section_id', 'irwin-predictive-search');

      try {
        const response = await fetch(url.toString(), {
          signal: request.signal,
          headers: { Accept: 'text/html' }
        });
        const responseText = await response.text();
        if (!response.ok) throw new Error(`Predictive search failed with ${response.status}`);
        if (input.value.trim() !== query) return;

        const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const responseSection = responseDocument.querySelector('#shopify-section-irwin-predictive-search');
        if (!responseSection) throw new Error('Predictive search section was not returned');

        resultsContent.innerHTML = responseSection.innerHTML;
        if (loading) loading.hidden = true;
        setResultsExpanded(true);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        showSearchError();
      } finally {
        if (searchRequest === request) searchRequest = null;
      }
    };

    input?.addEventListener('input', () => {
      window.clearTimeout(searchTimer);
      const query = input.value.trim();
      if (!query) {
        resetResults();
        return;
      }
      if (resultsContent) resultsContent.replaceChildren();
      if (loading) loading.hidden = false;
      setResultsExpanded(true);
      searchTimer = window.setTimeout(fetchPredictiveResults, 220);
    });

    const closeSearch = () => {
      window.clearTimeout(searchTimer);
      searchRequest?.abort();
      searchRequest = null;
      if (loading) loading.hidden = true;
      overlay.classList.remove('open');
      overlay.setAttribute('aria-hidden', 'true');
      body.classList.remove('irwin-search-open');
      if (lastSearchTrigger?.isConnected) lastSearchTrigger.focus();
      lastSearchTrigger = null;
    };

    qsa('[data-search-open]').forEach((button) => {
      if (!prepare(button, 'irwinSearchOpenReady')) return;
      button.addEventListener('click', () => {
        const openMenu = qs('[data-mobile-nav].open');
        const openMenuButton = qs('[data-menu-button][aria-expanded="true"]');
        openMenu?.classList.remove('open');
        openMenu?.setAttribute('aria-hidden', 'true');
        openMenuButton?.setAttribute('aria-expanded', 'false');
        body.classList.remove('irwin-menu-open');
        lastSearchTrigger = button;
        overlay.classList.add('open');
        overlay.setAttribute('aria-hidden', 'false');
        body.classList.add('irwin-search-open');
        if (input?.value.trim() && !resultsContent?.children.length) {
          if (loading) loading.hidden = false;
          setResultsExpanded(true);
          searchTimer = window.setTimeout(fetchPredictiveResults, 0);
        }
        window.setTimeout(() => input?.focus(), reduceMotion ? 0 : 180);
      });
    });

    qsa('[data-search-close]', overlay).forEach((button) => button.addEventListener('click', closeSearch));
    overlay.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSearch();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = qsa(focusableSelector, panel).filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && doc.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && doc.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  const getCartDrawer = () => qs('[data-cart-drawer]');

  const closeCartDrawer = () => {
    const drawer = getCartDrawer();
    if (!drawer || !drawer.classList.contains('active')) return;
    window.clearTimeout(cartCloseTimer);
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    cartCloseTimer = window.setTimeout(() => {
      drawer.classList.remove('active', 'is-loading');
      body.classList.remove('irwin-cart-open');
      if (lastCartTrigger?.isConnected) lastCartTrigger.focus();
      lastCartTrigger = null;
    }, reduceMotion ? 0 : 460);
  };

  const openCartDrawer = ({ focus = true } = {}) => {
    const drawer = getCartDrawer();
    if (!drawer) return;
    window.clearTimeout(cartCloseTimer);
    drawer.classList.add('active');
    drawer.setAttribute('aria-hidden', 'false');
    body.classList.add('irwin-cart-open');
    if (!drawer.classList.contains('open')) {
      if (reduceMotion) drawer.classList.add('open');
      else window.requestAnimationFrame(() => window.requestAnimationFrame(() => drawer.classList.add('open')));
    }
    if (focus) {
      window.setTimeout(() => qs('.irwin-cart-drawer__panel', drawer)?.focus(), reduceMotion ? 0 : 450);
    }
  };

  const replaceCartDrawerMarkup = (markup, stayOpen = false) => {
    const section = qs('#shopify-section-cart-drawer');
    if (!section || !markup) return false;
    const wasOpen = getCartDrawer()?.classList.contains('open');
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const replacement = qs('#shopify-section-cart-drawer', parsed);
    if (replacement) section.replaceWith(replacement);
    else section.innerHTML = markup;

    const drawer = getCartDrawer();
    if (stayOpen && drawer) {
      drawer.classList.add('active');
      if (wasOpen || reduceMotion) drawer.classList.add('open');
      else window.requestAnimationFrame(() => window.requestAnimationFrame(() => drawer.classList.add('open')));
      drawer.classList.remove('is-loading');
      drawer.setAttribute('aria-hidden', 'false');
      body.classList.add('irwin-cart-open');
    }
    return true;
  };

  const refreshCartDrawer = async (openAfter = false) => {
    const drawer = getCartDrawer();
    const stayOpen = openAfter || drawer?.classList.contains('active');
    if (!qs('#shopify-section-cart-drawer')) return false;
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart?section_id=cart-drawer`);
    if (!response.ok) return false;
    const markup = await response.text();
    return replaceCartDrawerMarkup(markup, stayOpen);
  };

  const updateCartLine = async (line, nextQuantity, input = null) => {
    const drawer = getCartDrawer();
    if (!drawer || drawer.classList.contains('is-loading')) return;
    const quantity = Math.max(0, Number(nextQuantity) || 0);
    if (!line) return;

    if (input && quantity > 0) input.value = quantity;
    drawer.classList.add('is-loading');
    drawer.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'Unable to update cart');
      qsa('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
      await refreshCartDrawer(true);
    } catch (error) {
      const currentDrawer = getCartDrawer();
      currentDrawer?.classList.remove('is-loading');
      currentDrawer?.removeAttribute('aria-busy');
    }
  };

  const changeCartLine = (quantityControl, nextQuantity) => {
    const line = Number(quantityControl?.dataset.cartLine);
    const input = qs('[data-cart-quantity-input]', quantityControl);
    if (!line || !input) return;
    const quantity = Math.max(1, Number(nextQuantity) || 1);
    updateCartLine(line, quantity, input);
  };

  const initCartDrawer = () => {
    if (!prepare(body, 'irwinCartReady')) return;
    doc.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-drawer-open]')) {
        event.preventDefault();
        lastCartTrigger = event.target.closest('[data-cart-drawer-open]');
        openCartDrawer();
        return;
      }
      if (event.target.closest('[data-cart-drawer-close]')) {
        closeCartDrawer();
        return;
      }
      const removeButton = event.target.closest('[data-cart-remove]');
      if (removeButton) {
        event.preventDefault();
        const line = Number(removeButton.dataset.cartLine);
        if (line) updateCartLine(line, 0);
        return;
      }
      const quantityButton = event.target.closest('[data-cart-quantity-minus],[data-cart-quantity-plus]');
      if (!quantityButton) return;
      const control = quantityButton.closest('[data-cart-quantity]');
      const input = qs('[data-cart-quantity-input]', control);
      if (!control || !input) return;
      const direction = quantityButton.matches('[data-cart-quantity-plus]') ? 1 : -1;
      changeCartLine(control, Number(input.value) + direction);
    });
    doc.addEventListener('change', (event) => {
      if (!event.target.matches('[data-cart-quantity-input]')) return;
      const control = event.target.closest('[data-cart-quantity]');
      if (control) changeCartLine(control, event.target.value);
    });
    doc.addEventListener('irwin:external-cart-add', async () => {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Irwin-Custom-Color-Sync': '1' }
        });
        const cart = await response.json();
        if (response.ok) qsa('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
        await refreshCartDrawer(true);
        openCartDrawer({ focus: false });
      } catch (error) {
        // Globo's cart add succeeded; drawer refresh failure should not undo it.
      }
    });
    doc.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && getCartDrawer()?.classList.contains('active')) closeCartDrawer();
    });

    doc.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const drawer = getCartDrawer();
      if (!drawer?.classList.contains('open')) return;
      const focusable = qsa('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', drawer)
        .filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && doc.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && doc.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  const splitWords = (element) => {
    if (!element || element.dataset.wordsReady === 'true') return;
    const walker = doc.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        if (node.parentElement?.closest('.w,script,style')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);
    textNodes.forEach((node) => {
      const fragment = doc.createDocumentFragment();
      node.nodeValue.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) fragment.appendChild(doc.createTextNode(part));
        else {
          const word = doc.createElement('span');
          word.className = 'w';
          word.textContent = part;
          fragment.appendChild(word);
        }
      });
      node.replaceWith(fragment);
    });
    element.dataset.wordsReady = 'true';
  };

  const initReveals = () => {
    const targets = qsa('.rv,.lr,.imgrv,.ws').filter((element) => prepare(element, 'irwinRevealReady'));
    targets.filter((element) => element.classList.contains('ws')).forEach(splitWords);
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach((element) => element.classList.add('in'));
      return;
    }

    const observer = new IntersectionObserver((entries, instance) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('in');
        if (entry.target.classList.contains('ws')) {
          qsa('.w', entry.target).forEach((word, index) => {
            word.style.transitionDelay = `${Math.min(index * 28, 600)}ms`;
          });
        }
        instance.unobserve(entry.target);
      });
    }, { threshold: 0.14, rootMargin: '0px 0px -5% 0px' });

    targets.forEach((element) => observer.observe(element));
  };

  const initParallax = () => {
    if (reduceMotion) return;
    const items = qsa('[data-parallax],.plxban .bg').filter((element) => prepare(element, 'irwinParallaxReady'));
    if (!items.length) return;

    let scheduled = false;
    const render = () => {
      const viewport = window.innerHeight;
      items.forEach((element) => {
        const rect = element.parentElement.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > viewport) return;
        const progress = (viewport - rect.top) / (viewport + rect.height);
        const travel = Number(element.dataset.travel || 170);
        element.style.transform = `translate3d(0, ${(progress - 0.5) * travel}px, 0)`;
      });
      scheduled = false;
    };

    const requestRender = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(render);
    };

    render();
    window.addEventListener('scroll', requestRender, { passive: true });
    window.addEventListener('resize', requestRender);
  };

  const initScrubText = () => {
    const scrubItems = qsa('.scrub').filter((element) => prepare(element, 'irwinScrubReady'));
    scrubItems.forEach(splitWords);
    if (!scrubItems.length || reduceMotion) return;

    const update = () => {
      const viewport = window.innerHeight;
      scrubItems.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const progress = Math.max(0, Math.min(1, (viewport * 0.92 - rect.top) / (rect.height + viewport * 0.32)));
        const words = qsa('.w', element);
        const litCount = Math.ceil(words.length * progress);
        words.forEach((word, index) => word.classList.toggle('lit', index < litCount));
      });
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
  };

  const initCounters = () => {
    qsa('.stats').forEach((stats) => {
      const counters = qsa('.count[data-to]', stats);
      if (!counters.length || !prepare(stats, 'irwinCountersReady')) return;

      const complete = () => counters.forEach((counter) => {
        counter.textContent = String(Number(counter.dataset.to) || 0);
      });
      if (reduceMotion || !('IntersectionObserver' in window)) {
        complete();
        return;
      }

      const run = () => counters.forEach((counter) => {
        const target = Number(counter.dataset.to) || 0;
        const started = performance.now();
        const duration = 1700;
        const step = (time) => {
          const progress = Math.min((time - started) / duration, 1);
          counter.textContent = String(Math.round(target * (1 - Math.pow(1 - progress, 3))));
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });

      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        run();
        observer.disconnect();
      }, { threshold: 0.3 });
      observer.observe(stats);
    });
  };

  const initFillHeadings = () => {
    qsa('[data-fill-heading]').forEach((heading) => {
      if (heading.dataset.fillReady === 'true') return;
      const accentWord = (heading.dataset.accentWord || '').toLowerCase();
      const words = heading.textContent.trim().split(/\s+/);
      heading.innerHTML = words.map((word) => {
        const clean = word.replace(/[^a-z0-9]/gi, '').toLowerCase();
        const chars = Array.from(word).map((character) => `<span class="ch">${character}</span>`).join('');
        return `<span class="wd${clean === accentWord ? ' acc' : ''}">${chars}</span>`;
      }).join(' ');
      heading.dataset.fillReady = 'true';
    });

    const fillHeadings = qsa('[data-fill-heading]').filter((heading) => prepare(heading, 'irwinFillScrollReady'));
    if (!fillHeadings.length) return;

    const update = () => {
      const viewport = window.innerHeight;
      fillHeadings.forEach((heading) => {
        const section = heading.closest('section') || heading;
        const rect = section.getBoundingClientRect();
        const progress = Math.max(0, Math.min(1, (viewport - rect.top) / Math.max(rect.height, viewport * 0.8)));
        const chars = qsa('.ch', heading);
        const count = Math.ceil(chars.length * progress);
        chars.forEach((char, index) => char.classList.toggle('lit', index < count));
      });
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
  };

  const initPanels = () => {
    qsa('[data-expanding-panels]').forEach((container) => {
      if (!prepare(container, 'irwinPanelsReady')) return;
      const panels = qsa('[data-panel]', container);
      panels.forEach((panel) => {
        const activate = () => {
          panels.forEach((item) => item.classList.toggle('open', item === panel));
          container.classList.add('engaged');
        };
        panel.addEventListener('mouseenter', activate);
        panel.addEventListener('focusin', activate);
        panel.addEventListener('click', activate);
      });
    });
  };

  const initHexagons = () => {
    qsa('[data-hex-group]').forEach((group) => {
      if (!prepare(group, 'irwinHexReady')) return;
      qsa('[data-hex]', group).forEach((hex) => {
        hex.addEventListener('click', () => {
          qsa('[data-hex]', group).forEach((item) => {
            item.classList.toggle('on', item === hex);
            item.classList.toggle('active', item === hex);
            item.setAttribute('aria-pressed', String(item === hex));
          });
          const label = qs('[data-hex-label]', group);
          if (label) label.textContent = hex.dataset.label || hex.textContent.trim();
          const stage = qs('[data-color-stage]', group.closest('.color-grid'));
          const stageImage = stage ? qs('img', stage) : null;
          if (stage && stageImage && hex.dataset.stageImage) {
            stage.classList.add('changing');
            window.setTimeout(() => {
              stageImage.src = hex.dataset.stageImage;
              stageImage.alt = hex.dataset.label || stageImage.alt;
              stage.classList.remove('changing');
            }, 220);
          }
        });
      });
    });
  };

  const initCategorySwitches = () => {
    qsa('[data-category-card]').forEach((card) => {
      if (!prepare(card, 'irwinCategorySwitchReady')) return;
      const buttons = qsa('[data-category-switch]', card);
      const images = qsa('[data-category-image]', card);
      const title = qs('[data-cat-title]', card);
      const kicker = qs('[data-cat-kicker]', card);
      const copy = qs('[data-cat-copy]', card);
      const cta = qs('[data-cat-cta]', card);
      const link = qs('[data-cat-link]', card);

      const activate = (button) => {
        const imageIndex = Number(button.dataset.image || 0);
        card.classList.add('changing');
        window.setTimeout(() => {
          buttons.forEach((item) => item.classList.toggle('active', item === button));
          images.forEach((image, index) => image.classList.toggle('active', index === imageIndex));
          if (title) title.textContent = button.dataset.title || '';
          if (kicker) kicker.textContent = button.dataset.kicker || '';
          if (copy) copy.textContent = button.dataset.copy || '';
          if (cta) cta.textContent = button.dataset.cta || '';
          if (link && button.dataset.url) link.href = button.dataset.url;
          card.classList.remove('changing');
        }, 180);
      };

      buttons.forEach((button) => button.addEventListener('click', () => activate(button)));
    });

    qsa('[data-category-duo]').forEach((duo) => {
      if (!prepare(duo, 'irwinCategoryDuoReady')) return;
      qsa('[data-category-card]', duo).forEach((card) => {
        card.addEventListener('mouseenter', () => {
          duo.classList.add('engaged');
          qsa('[data-category-card]', duo).forEach((item) => item.classList.toggle('active-panel', item === card));
        });
      });
      duo.addEventListener('mouseleave', () => {
        duo.classList.remove('engaged');
        qsa('[data-category-card]', duo).forEach((item) => item.classList.remove('active-panel'));
      });
    });
  };

  const initAccordions = () => {
    qsa('[data-accordion-group]').forEach((group) => {
      if (!prepare(group, 'irwinAccordionReady')) return;
      qsa('[data-accordion-item]', group).forEach((item) => {
        const button = qs('[data-accordion-button]', item);
        if (!button) return;
        button.addEventListener('click', () => {
          const opening = !item.classList.contains('open');
          qsa('[data-accordion-item]', group).forEach((entry) => {
            entry.classList.toggle('open', opening && entry === item);
            const entryButton = qs('[data-accordion-button]', entry);
            if (entryButton) entryButton.setAttribute('aria-expanded', String(opening && entry === item));
          });
        });
      });
    });
  };

  const initSliders = () => {
    qsa('[data-slider]').forEach((slider) => {
      if (!prepare(slider, 'irwinSliderReady')) return;
      const track = qs('[data-slider-track]', slider);
      if (!track) return;
      const distance = () => Math.min(track.clientWidth * 0.82, 420);
      qs('[data-slider-prev]', slider)?.addEventListener('click', () => track.scrollBy({ left: -distance(), behavior: 'smooth' }));
      qs('[data-slider-next]', slider)?.addEventListener('click', () => track.scrollBy({ left: distance(), behavior: 'smooth' }));
    });
  };

  const initProjectFilters = () => {
    qsa('[data-project-filters]').forEach((filterRoot) => {
      if (!prepare(filterRoot, 'irwinProjectFiltersReady')) return;
      const buttons = qsa('[data-project-filter]', filterRoot);
      const grid = filterRoot.nextElementSibling;
      if (!grid) return;
      const cards = qsa('[data-project-category]', grid);
      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const filter = button.dataset.projectFilter;
          buttons.forEach((item) => item.classList.toggle('active', item === button));
          cards.forEach((card) => {
            card.hidden = filter !== 'all' && card.dataset.projectCategory !== filter;
          });
        });
      });
    });
  };

  const initTimelineNavigation = () => {
    qsa('.timeline-nav').forEach((navigation) => {
      if (!prepare(navigation, 'irwinTimelineReady')) return;
      const timeline = navigation.closest('.timeline');
      if (!timeline) return;
      const links = qsa('a[href^="#"]', navigation);
      const timelineList = qs('.timeline-list', timeline);
      const timelineWrap = navigation.closest('.timeline-wrap');

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            links.forEach((link) => link.classList.toggle('active', link.getAttribute('href') === `#${entry.target.id}`));
          });
        }, { rootMargin: '-35% 0px -50% 0px', threshold: 0 });
        qsa('.milestone[id]', timeline).forEach((milestone) => observer.observe(milestone));
      }

      if (timelineList && qs('[data-timeline-progress]', navigation)) {
        let frame = 0;
        const updateTimeline = () => {
          frame = 0;
          const rect = timelineList.getBoundingClientRect();
          const start = Math.min(window.innerHeight * 0.42, 360);
          const distance = Math.max(1, rect.height - window.innerHeight * 0.5);
          const progress = Math.max(0, Math.min(1, (start - rect.top) / distance));
          navigation.style.setProperty('--timeline-progress', `${progress * 100}%`);

          if (!timelineWrap || window.innerWidth <= 760) {
            navigation.classList.remove('is-fixed', 'is-ended');
            navigation.style.removeProperty('--history-left');
            navigation.style.removeProperty('--history-width');
            return;
          }

          const wrapRect = timelineWrap.getBoundingClientRect();
          const pinTop = window.innerWidth <= 1100 ? 90 : 110;
          const firstTrack = window.getComputedStyle(timelineWrap).gridTemplateColumns.split(' ')[0];
          const trackWidth = Number.parseFloat(firstTrack) || navigation.offsetWidth;
          const navHeight = navigation.offsetHeight;
          navigation.style.setProperty('--history-left', `${wrapRect.left}px`);
          navigation.style.setProperty('--history-width', `${trackWidth}px`);
          navigation.classList.toggle('is-fixed', wrapRect.top <= pinTop && wrapRect.bottom > pinTop + navHeight);
          navigation.classList.toggle('is-ended', wrapRect.bottom <= pinTop + navHeight);
        };
        const requestUpdate = () => {
          if (frame) return;
          frame = window.requestAnimationFrame(updateTimeline);
        };
        window.addEventListener('scroll', requestUpdate, { passive: true });
        window.addEventListener('resize', requestUpdate);
        updateTimeline();
      }
    });
  };

  const initScrollSwaps = () => {
    qsa('[data-scroll-swap]').forEach((root) => {
      if (!prepare(root, 'irwinScrollSwapReady')) return;
      const stage = qs('[data-scroll-swap-stage]', root);
      const images = qsa('[data-scroll-swap-image]', root);
      const steps = qsa('.identity-scroll__steps span', root);
      if (!stage || images.length < 2) return;

      let frame = 0;
      const update = () => {
        frame = 0;
        const rect = stage.getBoundingClientRect();
        const start = Math.min(window.innerHeight * 0.22, 180);
        const distance = Math.max(1, rect.height - window.innerHeight * 0.72);
        const progress = Math.max(0, Math.min(1, (start - rect.top) / distance));
        const activeIndex = progress >= 0.5 ? 1 : 0;
        root.dataset.swapActive = String(activeIndex);
        root.style.setProperty('--identity-progress', String(progress));
        images.forEach((image, index) => {
          const active = index === activeIndex;
          image.classList.toggle('active', active);
          image.setAttribute('aria-hidden', String(!active));
        });
        steps.forEach((step, index) => step.classList.toggle('active', index === activeIndex));
      };
      const requestUpdate = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(update);
      };
      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate);
      update();
    });
  };

  const initImageSliders = () => {
    qsa('[data-image-slider]').forEach((slider) => {
      if (!prepare(slider, 'irwinImageSliderReady')) return;
      const slides = qsa('[data-image-slider-slide]', slider);
      const dots = qsa('[data-image-slider-dot]', slider);
      if (slides.length < 2) return;
      let activeIndex = 0;

      const show = (index) => {
        activeIndex = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === activeIndex;
          slide.classList.toggle('active', active);
          slide.setAttribute('aria-hidden', String(!active));
          const video = qs('[data-image-slider-video]', slide);
          if (video) {
            if (active) {
              const playPromise = video.play();
              if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
            } else {
              video.pause();
            }
          }
        });
        dots.forEach((dot, dotIndex) => {
          const active = dotIndex === activeIndex;
          dot.classList.toggle('active', active);
          dot.setAttribute('aria-pressed', String(active));
        });
      };

      qs('[data-image-slider-prev]', slider)?.addEventListener('click', () => show(activeIndex - 1));
      qs('[data-image-slider-next]', slider)?.addEventListener('click', () => show(activeIndex + 1));
      dots.forEach((dot) => dot.addEventListener('click', () => show(Number(dot.dataset.imageSliderDot))));
      slider.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); show(activeIndex - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); show(activeIndex + 1); }
      });
      show(0);
    });
  };

  const initCollectionFilters = () => {
    qsa('[data-collection-filter-toggle]').forEach((button) => {
      if (!prepare(button, 'irwinCollectionFilterReady')) return;
      const filters = qs(`#${button.getAttribute('aria-controls')}`);
      if (!filters) return;
      button.addEventListener('click', () => {
        const opening = !filters.classList.contains('open');
        filters.classList.toggle('open', opening);
        button.setAttribute('aria-expanded', String(opening));
      });
    });
  };

  const initGalleries = () => {
    qsa('[data-product-gallery]').forEach((gallery) => {
      if (!prepare(gallery, 'irwinGalleryReady')) return;
      const mainImage = qs('[data-main-image]', gallery);
      const mainLink = qs('[data-main-image-link]', gallery);
      const mainStage = qs('[data-main-stage]', gallery);
      const magnifier = qs('[data-image-magnifier]', gallery);
      const productGrid = gallery.closest('.product-grid');
      let pinFrame = 0;
      const updateGalleryPin = () => {
        pinFrame = 0;
        if (!productGrid || window.innerWidth <= 1180) {
          gallery.classList.remove('is-scroll-fixed', 'is-scroll-ended');
          gallery.style.removeProperty('--irwin-gallery-left');
          gallery.style.removeProperty('--irwin-gallery-width');
          return;
        }

        const gridRect = productGrid.getBoundingClientRect();
        const firstTrack = window.getComputedStyle(productGrid).gridTemplateColumns.split(' ')[0];
        const columnWidth = Number.parseFloat(firstTrack) || gallery.offsetWidth;
        const galleryHeight = gallery.offsetHeight;
        const pinTop = 92;
        const canPin = productGrid.offsetHeight > galleryHeight;
        gallery.style.setProperty('--irwin-gallery-left', `${gridRect.left}px`);
        gallery.style.setProperty('--irwin-gallery-width', `${columnWidth}px`);
        gallery.classList.toggle('is-scroll-fixed', canPin && gridRect.top <= pinTop && gridRect.bottom > pinTop + galleryHeight);
        gallery.classList.toggle('is-scroll-ended', canPin && gridRect.bottom <= pinTop + galleryHeight);
      };
      const requestGalleryPin = () => {
        if (pinFrame) return;
        pinFrame = window.requestAnimationFrame(updateGalleryPin);
      };

      window.addEventListener('scroll', requestGalleryPin, { passive: true });
      window.addEventListener('resize', requestGalleryPin);
      updateGalleryPin();
      const updateMagnifierSource = () => {
        if (!mainImage || !magnifier) return;
        const source = mainImage.currentSrc || mainImage.src;
        magnifier.style.backgroundImage = source ? `url("${source.replace(/"/g, '\\"')}")` : '';
      };

      if (mainStage && mainImage && magnifier && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        mainImage.addEventListener('load', updateMagnifierSource);
        mainStage.addEventListener('pointerenter', () => {
          updateMagnifierSource();
          mainStage.classList.add('is-magnifying');
        });
        mainStage.addEventListener('pointermove', (event) => {
          const rect = mainStage.getBoundingClientRect();
          const x = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
          const y = Math.max(0, Math.min(rect.height, event.clientY - rect.top));
          const zoom = 2.2;
          magnifier.style.left = `${x}px`;
          magnifier.style.top = `${y}px`;
          magnifier.style.backgroundSize = `${rect.width * zoom}px ${rect.height * zoom}px`;
          magnifier.style.backgroundPosition = `${-(x * zoom - magnifier.offsetWidth / 2)}px ${-(y * zoom - magnifier.offsetHeight / 2)}px`;
        });
        mainStage.addEventListener('pointerleave', () => mainStage.classList.remove('is-magnifying'));
      }
      const galleryThumbs = qsa('[data-gallery-thumb]', gallery);
      const imageCache = new Map();
      let galleryRequest = 0;

      const preloadGalleryImage = (src) => {
        if (!src) return Promise.resolve();
        if (imageCache.has(src)) return imageCache.get(src);

        const image = new Image();
        image.decoding = 'async';
        image.src = src;

        const ready = typeof image.decode === 'function'
          ? image.decode().catch(() => new Promise((resolve) => {
              if (image.complete) { resolve(); return; }
              image.addEventListener('load', resolve, { once: true });
              image.addEventListener('error', resolve, { once: true });
            }))
          : new Promise((resolve) => {
              if (image.complete) { resolve(); return; }
              image.addEventListener('load', resolve, { once: true });
              image.addEventListener('error', resolve, { once: true });
            });

        imageCache.set(src, ready);
        return ready;
      };

      // Warm the gallery cache before the customer starts browsing.
      // The first few images start immediately; the rest preload as soon as
      // the browser has idle time so thumbnail changes stay responsive.
      galleryThumbs.forEach((thumb, index) => {
        const src = thumb.dataset.image;
        if (!src || (mainImage && src === mainImage.currentSrc)) return;
        if (index < 4) {
          preloadGalleryImage(src);
        } else if ('requestIdleCallback' in window) {
          window.requestIdleCallback(() => preloadGalleryImage(src), { timeout: 1200 });
        } else {
          window.setTimeout(() => preloadGalleryImage(src), 250 + (index * 60));
        }
      });

      galleryThumbs.forEach((thumb) => {
        thumb.addEventListener('mouseenter', () => preloadGalleryImage(thumb.dataset.image), { passive: true });
        thumb.addEventListener('focus', () => preloadGalleryImage(thumb.dataset.image), { passive: true });
        thumb.addEventListener('click', () => {
          galleryThumbs.forEach((item) => item.classList.toggle('active', item === thumb));

          const nextSrc = thumb.dataset.image;
          const nextAlt = thumb.dataset.alt || '';
          const requestId = ++galleryRequest;

          if (!mainImage || !nextSrc) return;

          preloadGalleryImage(nextSrc).then(() => {
            if (requestId !== galleryRequest) return;

            mainStage?.classList.add('changing');
            mainImage.src = nextSrc;
            mainImage.removeAttribute('srcset');
            mainImage.removeAttribute('sizes');
            mainImage.alt = nextAlt;
            if (mainLink) mainLink.href = nextSrc;

            window.requestAnimationFrame(() => {
              window.requestAnimationFrame(() => {
                mainStage?.classList.remove('changing');
                updateMagnifierSource();
              });
            });
          });
        });
      });
    });
  };

  const initDialogs = () => {
    const focusableSelector = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    const restoreDialogFocus = (dialog) => {
      body.classList.remove('irwin-dialog-open');
      if (dialog._irwinTrigger?.isConnected) dialog._irwinTrigger.focus();
      dialog._irwinTrigger = null;
    };
    const closeDialog = (dialog) => {
      if (!dialog) return;
      if (typeof dialog.close === 'function' && dialog.open) {
        dialog.close();
        return;
      }
      dialog.hidden = true;
      dialog.classList.remove('open');
      dialog.setAttribute('aria-hidden', 'true');
      restoreDialogFocus(dialog);
    };
    const focusDialog = (dialog) => {
      window.requestAnimationFrame(() => {
        const focusable = qsa(focusableSelector, dialog).filter((element) => element.offsetParent !== null);
        (focusable[0] || dialog).focus?.();
      });
    };

    qsa('[data-dialog-open]').forEach((button) => {
      if (!prepare(button, 'irwinDialogOpenReady')) return;
      const dialog = qs(`#${button.dataset.dialogOpen}`);
      if (!dialog) return;
      button.addEventListener('click', () => {
        dialog._irwinTrigger = button;
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.hidden = false;
          dialog.classList.add('open');
          dialog.setAttribute('aria-hidden', 'false');
        }
        body.classList.add('irwin-dialog-open');
        focusDialog(dialog);
      });
    });
    qsa('[data-dialog-close]').forEach((button) => {
      if (!prepare(button, 'irwinDialogCloseReady')) return;
      button.addEventListener('click', () => {
        closeDialog(button.closest('dialog, [role="dialog"]'));
      });
    });
    qsa('dialog, [role="dialog"][id]').forEach((dialog) => {
      if (!prepare(dialog, 'irwinDialogReady')) return;
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeDialog(dialog);
      });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeDialog(dialog);
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = qsa(focusableSelector, dialog).filter((element) => element.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && doc.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && doc.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
      if (typeof dialog.close === 'function') dialog.addEventListener('close', () => restoreDialogFocus(dialog));
    });
  };

  const initProductForms = () => {
    qsa('[data-irwin-product]').forEach((root) => {
      const form = qs('[data-irwin-product-form]', root);
      const jsonNode = qs('[data-product-variants]', root);
      if (!form || !jsonNode) return;
      if (!prepare(root, 'irwinProductReady')) return;

      let variants = [];
      try {
        variants = JSON.parse(jsonNode.textContent);
      } catch (error) {
        console.error('Unable to parse product variants', error);
        return;
      }

      const optionGroups = qsa('[data-option-position]', root);
      const variantInput = qs('input[name="id"]', form);
      const addButton = qs('[data-product-submit]', form);
      const addText = qs('[data-product-submit-text]', form);
      const prices = qsa('[data-product-price]');
      const comparePrice = qs('[data-product-compare-price]', root);
      const sku = qs('[data-product-sku]', root);
      const mobileSelection = qs('[data-mobile-selection]');
      const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
      const unavailableText = root.dataset.unavailableText || 'Unavailable';
      const addErrorText = root.dataset.addErrorText || 'Unable to add this item to cart.';
      const customColorEnabled = root.dataset.customColorEnabled === 'true';
      const customColorPropertyInput = qs('[data-custom-color-property]', form);
      const customColorUpgradeHandle = root.dataset.customColorUpgradeHandle || 'custom-color-upgrade';
      const productTitle = root.dataset.productTitle || '';
      let customColorUpgradeVariantId = root.dataset.customColorUpgradeVariantId || '';

      const getConfirmedCustomColor = () => customColorEnabled ? String(customColorPropertyInput?.value || '').trim() : '';
      const getRequestedQuantity = () => Math.max(1, Number(qs('input[name="quantity"]', form)?.value || 1));
      const getCurrentVariantId = () => Number(variantInput?.value || 0);
      const cartUrl = () => `${window.Shopify?.routes?.root || '/'}cart.js`;
      const fetchCartState = () => fetch(cartUrl(), { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((request) => request.json());
      const delay = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

      const isGloboOptionsActive = () => {
        if (qs('.gpo-clone-atc-button', root)) return true;
        const area = qs('[data-irwin-product-app-options]', root);
        if (!area) return false;
        const gpo = qs('.gpo-app, .gpo-container, [class*="gpo-"]', area);
        if (!gpo) return false;
        return !!qs('.gpo-element, [data-gpo-element], input:not([type="hidden"]), select, textarea, [class*="gpo-option"], [class*="gpo-input"]', gpo);
      };

      const variantQuantityInCart = (cart, variantId) => (cart?.items || []).reduce((total, item) => {
        return total + (Number(item.variant_id || item.id) === Number(variantId) ? Number(item.quantity || 0) : 0);
      }, 0);

      const matchingUpgradeQuantity = (cart, upgradeVariantId, colorName) => (cart?.items || []).reduce((total, item) => {
        const properties = item.properties || {};
        const matchesVariant = Number(item.variant_id || item.id) === Number(upgradeVariantId);
        const matchesProduct = String(properties['_Applied to'] || '') === String(productTitle || '');
        const matchesColor = String(properties['_Selected custom color'] || '') === String(colorName || '');
        return total + (matchesVariant && matchesProduct && matchesColor ? Number(item.quantity || 0) : 0);
      }, 0);

      const resolveCustomColorUpgradeVariantId = async () => {
        if (customColorUpgradeVariantId && Number(customColorUpgradeVariantId) > 0) return customColorUpgradeVariantId;

        const shopRoot = window.Shopify?.routes?.root || '/';
        const normalizedRoot = shopRoot.endsWith('/') ? shopRoot : `${shopRoot}/`;
        const productUrl = `${normalizedRoot}products/${encodeURIComponent(customColorUpgradeHandle)}.js`;
        const response = await fetch(productUrl, {
          method: 'GET',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Unable to load the custom color surcharge product.');

        const productData = await response.json();
        const firstVariant = Array.isArray(productData?.variants) ? productData.variants[0] : null;
        if (!firstVariant?.id) throw new Error('The custom color surcharge product has no purchasable variant.');

        customColorUpgradeVariantId = String(firstVariant.id);
        root.dataset.customColorUpgradeVariantId = customColorUpgradeVariantId;
        return customColorUpgradeVariantId;
      };

      // Globo options are submitted through the same Irwin AJAX product flow.
      // This keeps all line-item properties inside the product form, prevents
      // Globo/native form submission from redirecting to /cart, and lets the
      // custom-color surcharge stay synchronized with the configured product.

      const selectedOptions = () => optionGroups.map((group) => {
        const selected = qs('[data-option-value].active,[data-option-value][aria-pressed="true"]', group);
        return selected?.dataset.optionValue || '';
      });

      const findVariant = () => {
        if (optionGroups.length === 0) {
          return variants.find((variant) => String(variant.id) === String(variantInput?.value)) || variants[0];
        }
        const selected = selectedOptions();
        return variants.find((variant) => variant.options.every((value, index) => value === selected[index]));
      };

      const updateVariant = () => {
        const variant = findVariant();
        if (!variant) {
          if (addButton) addButton.disabled = true;
          if (addText) addText.textContent = unavailableText;
          return;
        }

        if (variantInput) variantInput.value = variant.id;
        prices.forEach((price) => { price.textContent = formatMoney(variant.price, moneyFormat); });
        if (comparePrice) {
          comparePrice.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price, moneyFormat) : '';
          comparePrice.hidden = !(variant.compare_at_price > variant.price);
        }
        if (sku) sku.textContent = variant.sku || '';
        if (mobileSelection) {
          const packageGroup = optionGroups.find((group) => /package/i.test(qs('.option-head h3', group)?.textContent || ''));
          const packageValue = packageGroup ? qs('[data-option-value].active,[data-option-value][aria-pressed="true"]', packageGroup)?.dataset.optionValue : '';
          if (packageValue) mobileSelection.textContent = packageValue;
        }
        if (addButton) addButton.disabled = !variant.available;
        if (addText) addText.textContent = variant.available ? addText.dataset.availableText : addText.dataset.soldoutText;

        if (variant.featured_image?.src) {
          const mainImage = qs('[data-main-image]', root);
          if (mainImage) {
            mainImage.src = variant.featured_image.src;
            mainImage.removeAttribute('srcset');
            mainImage.removeAttribute('sizes');
          }
          const mainLink = qs('[data-main-image-link]', root);
          if (mainLink) mainLink.href = variant.featured_image.src;
        }

        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', nextUrl);
        root.dispatchEvent(new CustomEvent('irwin:variant-change', { detail: variant }));
      };

      qsa('[data-option-value]', root).forEach((button) => {
        button.addEventListener('click', () => {
          const group = button.closest('[data-option-position]');
          qsa('[data-option-value]', group).forEach((item) => {
            const active = item === button;
            item.classList.toggle('active', active);
            item.setAttribute('aria-pressed', String(active));
          });
          const label = qs('[data-option-label]', group);
          if (label) label.textContent = button.dataset.optionValue;
          updateVariant();
        });
      });

      const quantity = qs('input[name="quantity"]', form);
      qs('[data-qty-minus]', form)?.addEventListener('click', () => {
        quantity.value = Math.max(1, Number(quantity.value || 1) - 1);
      });
      qs('[data-qty-plus]', form)?.addEventListener('click', () => {
        quantity.value = Math.max(1, Number(quantity.value || 1) + 1);
      });

      form.addEventListener('submit', async (event) => {
        if (!window.fetch) return;

        const globoActive = isGloboOptionsActive();

        // Always keep product adds inside the Irwin AJAX flow. Globo's default
        // form submission can redirect custom themes to /cart; stopping that
        // submit here preserves the configured properties while keeping the
        // existing Irwin drawer behaviour.
        event.preventDefault();
        if (globoActive) {
          event.stopImmediatePropagation();
          if (window.GPOConfigs) window.GPOConfigs.cart_type = 'drawer';
        }

        if (addButton) addButton.classList.add('loading');
        const errorNode = qs('[data-product-error]', form);
        if (errorNode) errorNode.textContent = '';

        try {
          lastCartTrigger = addButton || form;
          openCartDrawer({ focus: false });
          getCartDrawer()?.classList.add('is-loading');

          // Constructing FormData fires the native `formdata` event, so Globo
          // and the Irwin custom-colour handler can contribute their current
          // line-item properties before the request is sent.
          const formData = new FormData(form);
          const confirmedCustomColor = getConfirmedCustomColor();
          const shopRoot = window.Shopify?.routes?.root || '/';
          let result = null;

          if (globoActive) {
            // Resolve the surcharge before the main item is added. This avoids
            // ever knowingly adding a custom-colour rack when the upgrade
            // product itself cannot be resolved.
            let upgradeVariantId = null;
            if (confirmedCustomColor) {
              upgradeVariantId = Number(await resolveCustomColorUpgradeVariantId());
              if (!upgradeVariantId) throw new Error('The custom color upgrade is currently unavailable.');
            }

            // Send Globo's form exactly as it exists. This retains all named
            // properties/option values the app injected into the product form.
            formData.set('id', String(getCurrentVariantId() || formData.get('id') || ''));
            formData.set('quantity', String(Math.max(1, Number(formData.get('quantity') || 1))));
            formData.set('sections', 'cart-drawer');
            formData.set('sections_url', window.location.pathname);

            const mainResponse = await fetch(`${shopRoot}cart/add.js`, {
              method: 'POST',
              headers: { Accept: 'application/json' },
              body: formData
            });
            const mainResult = await mainResponse.json();
            if (!mainResponse.ok) throw new Error(mainResult.description || addErrorText);
            result = mainResult;

            if (confirmedCustomColor && upgradeVariantId) {
              const surchargeQuantity = Math.max(1, Number(formData.get('quantity') || 1));
              const surchargePayload = {
                items: [{
                  id: upgradeVariantId,
                  quantity: surchargeQuantity,
                  properties: {
                    '_Applied to': productTitle,
                    '_Selected custom color': confirmedCustomColor
                  }
                }]
              };

              let surchargeResponse = await fetch(`${shopRoot}cart/add.js`, {
                method: 'POST',
                headers: {
                  Accept: 'application/json',
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(surchargePayload)
              });
              let surchargeResult = await surchargeResponse.json();

              // One quick retry protects against a transient cart request race
              // between the app-configured product and the surcharge item.
              if (!surchargeResponse.ok) {
                await delay(180);
                surchargeResponse = await fetch(`${shopRoot}cart/add.js`, {
                  method: 'POST',
                  headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify(surchargePayload)
                });
                surchargeResult = await surchargeResponse.json();
              }

              if (!surchargeResponse.ok) {
                throw new Error(surchargeResult.description || 'Unable to add the custom color upgrade.');
              }
            }
          } else if (confirmedCustomColor) {
            // Standard Irwin products can add the configured item and surcharge
            // atomically in one Shopify request.
            const upgradeVariantId = await resolveCustomColorUpgradeVariantId();
            const quantity = Math.max(1, Number(formData.get('quantity') || 1));
            const mainVariantId = formData.get('id');
            const mainProperties = {};

            for (const [key, value] of formData.entries()) {
              const match = /^properties\[(.+)\]$/.exec(key);
              if (match && String(value || '').trim()) mainProperties[match[1]] = value;
            }

            const mainItem = {
              id: Number(mainVariantId),
              quantity,
              properties: mainProperties
            };
            const sellingPlan = formData.get('selling_plan');
            if (sellingPlan) mainItem.selling_plan = Number(sellingPlan);

            const response = await fetch(`${shopRoot}cart/add.js`, {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                items: [
                  mainItem,
                  {
                    id: Number(upgradeVariantId),
                    quantity,
                    properties: {
                      '_Applied to': productTitle,
                      '_Selected custom color': confirmedCustomColor
                    }
                  }
                ],
                sections: ['cart-drawer'],
                sections_url: window.location.pathname
              })
            });
            result = await response.json();
            if (!response.ok) throw new Error(result.description || addErrorText);
          } else {
            formData.set('sections', 'cart-drawer');
            formData.set('sections_url', window.location.pathname);
            const response = await fetch(`${shopRoot}cart/add.js`, {
              method: 'POST',
              headers: { Accept: 'application/json' },
              body: formData
            });
            result = await response.json();
            if (!response.ok) throw new Error(result.description || addErrorText);
          }

          // Refresh once after every related item has been added. This makes the
          // Globo rack behave exactly like the rest of the site: no navigation,
          // current cart count, and the Irwin drawer opens with the final contents.
          const [cart] = await Promise.all([
            fetchCartState(),
            refreshCartDrawer(true)
          ]);
          qsa('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
          openCartDrawer({ focus: false });
          root.dispatchEvent(new CustomEvent('irwin:item-added', { detail: result }));
        } catch (error) {
          if (errorNode) errorNode.textContent = error.message || addErrorText;
          getCartDrawer()?.classList.remove('is-loading');
          closeCartDrawer();
        } finally {
          if (addButton) addButton.classList.remove('loading');
        }
      }, true);

      updateVariant();
    });
  };

  const initContactSuccess = () => {
    qsa('[data-form-success]').forEach((message) => {
      if (!prepare(message, 'irwinFormSuccessReady')) return;
      if (window.location.hash === '#contact_form') message.scrollIntoView({ block: 'center' });
    });
  };

  const init = () => {
    initHeader();
    initSearch();
    initCartDrawer();
    initReveals();
    initParallax();
    initScrubText();
    initCounters();
    initFillHeadings();
    initPanels();
    initHexagons();
    initCategorySwitches();
    initAccordions();
    initSliders();
    initProjectFilters();
    initTimelineNavigation();
    initScrollSwaps();
    initImageSliders();
    initCollectionFilters();
    initGalleries();
    initDialogs();
    initProductForms();
    initContactSuccess();

    const loader = qs('[data-irwin-loader]');
    if (loader) window.setTimeout(() => {
      loader.classList.add('done');
      qs('#hero')?.classList.add('play');
    }, 900);
  };

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();

  doc.addEventListener('shopify:section:load', init);
})();
